class Tabs {
  constructor(tabCont) {
    this.$ = tabCont;
    this.buttonWrapper = this.$.querySelector('[role="tablist"]');
    this.btns = Array.from(this.$.querySelectorAll('[role="tab"]'));
    this.tabFocus = 0;

    // 必要なプロパティを初期化
    this.currentClass = 'is-current';
    this.hiddenClass = 'is-hidden';
    this.wrapper = this.$.closest('.js-module-tabs') || this.$;
    this.contents = Array.from(this.wrapper.querySelectorAll('[role="tabpanel"]'));

    // 🎯 自分が管理するタブIDのリストを作成
    this.managedTabIds = this.btns.map(btn => btn.getAttribute('aria-controls')).filter(Boolean);

    console.log("this.managedTabIds", this.managedTabIds);

    // URLハッシュがある場合
    this.hash = window.location.hash || null;
    this.condition = this.btns[0]?.getAttribute('aria-controls') || null;

    this.initialize();
    this.bind();

    // 静的メソッド用にインスタンスを登録 🎯
    Tabs.instances = Tabs.instances || [];
    Tabs.instances.push(this);
  }

  // 🔧 自分の管轄かどうかをチェックするメソッド
  isMyTab(tabId) {
    return this.managedTabIds.includes(tabId);
  }

  initialize() {
    // ✨ ハッシュ付きアクセスの場合
    if (this.hash) {
      const hashId = this.hash.replace('#', '');

      // 🎯 自分が管理するタブIDの場合のみ、ハッシュ処理を待つ
      if (this.isMyTab(hashId)) {
        // いったん全部リセット
        this.btns.forEach(btn => {
          btn.setAttribute('aria-selected', 'false');
          btn.setAttribute('tabindex', '0');
        });
        this.contents.forEach(panel => {
          panel.setAttribute('hidden', true);
        });

        // DOMContentLoadedで処理されるので、ここでは何もしない
        console.log(`⏳ タブ "${hashId}" はハッシュ着地で処理されます`);
        return;
      }

      // 🎯 ハッシュがパネル内要素を指している場合、ブラウザのネイティブスクロール前にパネルを表示
      // （hidden パネル内だとブラウザのスクロールが失敗するため、同期的に切り替える）
      const hashEl = document.getElementById(hashId);
      if (hashEl) {
        const parentPanel = hashEl.closest('[role="tabpanel"]');
        if (parentPanel && this.managedTabIds.includes(parentPanel.id)) {
          const targetBtn = this.btns.find(btn => btn.getAttribute('aria-controls') === parentPanel.id);
          if (targetBtn) {
            this.activateTab(targetBtn);
            console.log(`🎯 ハッシュ "${hashId}" のためパネル "${parentPanel.id}" を事前アクティブ化`);

            // iOS 以外：ブラウザのネイティブスクロールは画像ロード前で位置がずれるため
            // 全リソース読み込み完了後に scrollIntoView で正確な位置へ上書き
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            if (!isIOS) {
              const el = hashEl;
              window.addEventListener('load', () => {
                el.scrollIntoView({ behavior: 'instant', block: 'start' });
              }, { once: true });
            }
          }
          return;
        }
      }
    }

    // 🎯 HTMLに書かれている aria-selected="true" を探す
    const selectedTab = this.btns.find(btn =>
      btn.getAttribute('aria-selected') === 'true'
    );

    if (selectedTab) {
      // HTMLで指定されたタブをアクティブに
      const panelId = selectedTab.getAttribute('aria-controls');
      const targetPanel = document.getElementById(panelId);

      if (targetPanel) {
        targetPanel.removeAttribute('hidden');
      }

      // 他のパネルはhiddenに
      this.contents.forEach(panel => {
        if (panel.id !== panelId) {
          panel.setAttribute('hidden', true);
        }
      });

      console.log(`🎉 HTMLで指定されたタブ "${panelId}" をアクティブにしました！`);
    } else {
      // どれも選択されてない場合は最初のタブをアクティブに
      this.activateFirstTab();
    }
  }

  // 🌟 最初のタブをアクティブにするメソッド
  activateFirstTab() {
    if (this.btns.length > 0) {
      const firstTab = this.btns[0];
      const firstPanelId = firstTab.getAttribute('aria-controls');

      // 最初のタブを確実にアクティブに！
      firstTab.setAttribute('aria-selected', 'true');
      firstTab.setAttribute('tabindex', '-1');

      // 最初のパネルを表示
      const firstPanel = document.getElementById(firstPanelId);
      if (firstPanel) {
        firstPanel.removeAttribute('hidden');
      }

      console.log(`🎉 初期化完了！最初のタブ "${firstPanelId}" をアクティブにしました！`);
    }
  }

  bind() {
    this.btns.forEach((button) => {
      button.addEventListener('click', this.changeTabs.bind(this));
    });

    this.buttonWrapper.addEventListener("keydown", (e) => {
      // 右左キーでのタブ操作
      if (e.keyCode === 39 || e.keyCode === 37) {
        this.btns[this.tabFocus].setAttribute("tabindex", 0);

        if (e.keyCode === 39) { // 右キー
          this.tabFocus++;
          if (this.tabFocus >= this.btns.length) {
            this.tabFocus = 0;
          }
        } else if (e.keyCode === 37) { // 左キー
          this.tabFocus--;
          if (this.tabFocus < 0) {
            this.tabFocus = this.btns.length - 1;
          }
        }

        this.btns[this.tabFocus].setAttribute("tabindex", -1);
        this.btns[this.tabFocus].focus();
      }
    });
  }

  // ✨ タブアクティベート処理（スクロールなし）
  activateTab(tab) {
    const panelId = tab.getAttribute('aria-controls');

    // 🔧 自分の管轄外の場合は何もしない
    if (!this.isMyTab(panelId)) {
      return;
    }

    // 現在のセット内のタブを更新
    this.btns.forEach(btn => {
      const btnPanelId = btn.getAttribute('aria-controls');
      if (btnPanelId === panelId) {
        // 同じaria-controlsを持つ全てのボタンをオン
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '-1');
      } else {
        // 異なるaria-controlsを持つボタンはオフ
        btn.setAttribute('aria-selected', 'false');
        btn.setAttribute('tabindex', '0');
      }
    });

    // パネルを切り替え
    this.contents.forEach(panel => {
      panel.setAttribute('hidden', true);
    });

    const targetPanel = document.getElementById(panelId);
    if (targetPanel) {
      targetPanel.removeAttribute('hidden');
    }

    // 🎯 他のタブセットも同期（同じタブIDを持つもののみ）
    this.syncOtherTabSets(panelId);

    // 📱 SP表示時: アクティブなタブが左端から20px位置になるようスクロール
    if (window.matchMedia('(max-width: 767px)').matches) {
      const activeBtn = this.btns.find(btn => btn.getAttribute('aria-controls') === panelId);
      if (activeBtn && this.buttonWrapper) {
        this.buttonWrapper.scrollTo({
          left: activeBtn.offsetLeft - 20,
          behavior: 'smooth'
        });
      }
    }

    console.log(`✅ タブ "${panelId}" に切り替えました！`);
  }

  // 🔧 他のタブセットとの同期（該当するタブIDがある場合のみ）
  syncOtherTabSets(activePanelId) {
    const allTabContainers = document.querySelectorAll('.js-module-tabs');

    allTabContainers.forEach(container => {
      if (container === this.$) return; // 自分のセットはスキップ

      const tabs = container.querySelectorAll('[role="tab"]');
      // 🎯 該当するタブIDが存在するかチェック
      const hasMatchingTab = Array.from(tabs).some(tab =>
        tab.getAttribute('aria-controls') === activePanelId
      );

      // 該当するタブがある場合のみ同期処理
      if (hasMatchingTab) {
        tabs.forEach(tab => {
          const tabPanelId = tab.getAttribute('aria-controls');
          if (tabPanelId === activePanelId) {
            tab.setAttribute('aria-selected', 'true');
            tab.setAttribute('tabindex', '-1');
          } else {
            tab.setAttribute('aria-selected', 'false');
            tab.setAttribute('tabindex', '0');
          }
        });
      }
    });
  }

  // ✨ 通常のタブクリック処理
  changeTabs(e) {
    const target = e.target.closest('[role="tab"]') || e.target;

    if (target.getAttribute('role') === 'tab') {
      e.preventDefault();

      const panelId = target.getAttribute('aria-controls');

      // タブ切り替え
      this.activateTab(target);

      // ✨ アドレスバーにハッシュを付与（スクロールさせない）
      history.pushState(null, null, `#${panelId}`);

      // 🎯 data-scroll="true"がある場合のみスクロール
      const shouldScroll = target.getAttribute('data-scroll') === 'true';
      const targetPanel = document.getElementById(panelId);

      if (shouldScroll) {
        Tabs._scrollTo(targetPanel);
      }
    }
  }

  // ✨ ハッシュからのタブ切り替え（スクロールなし）
  switchTabByHash(targetTabId) {
    const targetTab = this.btns.find(btn =>
      btn.getAttribute('aria-controls') === targetTabId
    );

    if (targetTab) {
      this.activateTab(targetTab);
      return true;
    }
    return false;
  }

  // ✨ 静的メソッド：外部からタブを切り替え（ページ内ボタン用・スクロールあり）
  static switchToTab(targetTabId) {
    if (!Tabs.instances) return false;

    let foundInstance = null;

    // 該当するインスタンスを探す
    for (const instance of Tabs.instances) {
      if (instance.isMyTab(targetTabId)) {
        if (instance.switchTabByHash(targetTabId)) {
          foundInstance = instance;
          break;
        }
      }
    }

    if (!foundInstance) {
      console.warn(`⚠️ タブ "${targetTabId}" が見つかりませんでした`);
      return false;
    }

    // ✨ アドレスバーにハッシュを付与（追加！）
    history.pushState(null, null, `#${targetTabId}`);

    // ✨ スクロール処理（対象tabpanelへ）
    const targetPanel = document.getElementById(targetTabId);
    if (targetPanel) {
      Tabs._scrollTo(targetPanel);
    }
    return true;
  }

  // ✨ 静的メソッド：ハッシュ着地専用（スクロールなし）
  static switchToTabOnLoad(targetTabId) {
    if (!Tabs.instances) return false;

    // 該当するインスタンスを探してタブ切り替えだけ実行
    for (const instance of Tabs.instances) {
      if (instance.isMyTab(targetTabId)) {
        if (instance.switchTabByHash(targetTabId)) {
          console.log(`✅ ハッシュ着地でタブ "${targetTabId}" に切り替えました！`);
          return true;
        }
      }
    }

    console.warn(`⚠️ タブ "${targetTabId}" が見つかりませんでした`);
    return false;
  }

  static switchToTabAndScrollTo(panelId, targetEl) {
    if (!Tabs.instances) return false;

    let foundInstance = null;
    for (const instance of Tabs.instances) {
      if (instance.isMyTab(panelId)) {
        instance.switchTabByHash(panelId);
        foundInstance = instance;
        break;
      }
    }

    if (!foundInstance) return false;

    history.pushState(null, null, `#${targetEl.id || panelId}`);
    Tabs._scrollTo(targetEl);
    return true;
  }

  // ヘッダー高さを考慮したスクロール共通処理
  static _scrollTo(el, behavior = 'smooth') {
    if (!el) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // 対象要素にCSSで指定された scroll-margin-top を優先利用
    const computedStyle = getComputedStyle(el).scrollMarginTop;
    console.log("el", el);
    console.log("computedStyle", computedStyle);
    const cssScrollMarginTop = parseFloat(computedStyle || '0');
    console.log("cssScrollMarginTop", cssScrollMarginTop);

    const header = document.querySelector('.header__block');
    const headerHeight = header ? header.offsetHeight : 0;
    // 固定ナビのクラス名には依存せず、scroll-margin-top が未指定の時だけヘッダー高さで補完
    const fallbackOffset = headerHeight;
    const scrollOffset = Number.isFinite(cssScrollMarginTop) && cssScrollMarginTop > 0
      ? cssScrollMarginTop
      : fallbackOffset;

    setTimeout(() => {
      const top = el.getBoundingClientRect().top + window.pageYOffset - scrollOffset;
      window.scrollTo({ top, behavior });
    }, 200);
  }
}

// DOMContentLoaded処理
document.addEventListener('DOMContentLoaded', () => {
  // ✨ ページ読み込み時のハッシュ処理
  if (window.location.hash) {
    const hashId = window.location.hash.replace('#', '');

    // 🎯 タブのIDかどうかをチェック
    const isTabHash = Tabs.instances && Tabs.instances.some(instance =>
      instance.isMyTab(hashId)
    );

    if (isTabHash) {
      // ✨ タブの切り替えだけ実行（スクロールはブラウザに任せる）
      setTimeout(() => {
        Tabs.switchToTabOnLoad(hashId);
      }, 200);
    } else {
      // タブパネル内要素へのアンカーかチェック
      const targetEl = document.getElementById(hashId);
      if (targetEl) {
        const parentPanel = targetEl.closest('[role="tabpanel"]');
        if (parentPanel && parentPanel.hasAttribute('hidden')) {
          // タブ切り替え + 要素スクロール
          setTimeout(() => {
            Tabs.switchToTabAndScrollTo(parentPanel.id, targetEl);
          }, 200);
        } else if (parentPanel) {
          // パネルは表示中 → iOS Safari のみ JS でスクロール上書き
          // （iOS は scroll-margin-top が初期ロード時に不安定なため）
          // Chrome 等は CSS の scroll-margin-top に任せる
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          if (isIOS) {
            setTimeout(() => {
              Tabs._scrollTo(targetEl, 'auto');
            }, 200);
          }
        }
      }
    }
    // タブ以外のハッシュも通常通りブラウザに任せる
  }

  document.addEventListener('click', (e) => {
    // aタグのクリックかチェック
    const anchor = e.target.closest('a[href^="#"]');

    if (anchor) {
      const href = anchor.getAttribute('href');

      // hrefが#で始まる場合
      if (href && href.startsWith('#')) {
        const targetId = href.replace('#', '');

        // 🎯 タブのIDかどうかをチェック
        const isTabHash = Tabs.instances && Tabs.instances.some(instance =>
          instance.isMyTab(targetId)
        );

        if (isTabHash) {
          // ✨ タブの場合は、デフォルト動作を防いでswitchToTabを実行
          e.preventDefault();
          console.log(`🔗 アンカーリンク経由でタブ "${targetId}" へ切り替えます`);
          Tabs.switchToTab(targetId);
        } else {
          // タブパネル内要素へのアンカーかチェック
          const targetEl = document.getElementById(targetId);
          if (targetEl) {
            const parentPanel = targetEl.closest('[role="tabpanel"]');
            if (parentPanel) {
              e.preventDefault();
              if (parentPanel.hasAttribute('hidden')) {
                // タブ切り替え + 要素スクロール
                Tabs.switchToTabAndScrollTo(parentPanel.id, targetEl);
              } else {
                // タブは既に表示中 → 要素へ直接スクロール
                history.pushState(null, null, `#${targetId}`);
                Tabs._scrollTo(targetEl);
              }
            }
          }
        }
        // タブパネル外のアンカーリンクは通常通りブラウザに任せる
      }
    }
  });

});

// グローバル関数
// ページ内ボタンから呼ばれる・スクロールあり
window.switchToTab = function(targetTabId) {
  return Tabs.switchToTab(targetTabId);
};
// ページ内ボタンから呼ばれる・スクロールなし
window.switchToTabSilently = function(targetTabId) {
  return Tabs.switchToTabOnLoad(targetTabId);
};

// 初期化
const tabContents = Array.from(document.querySelectorAll('.js-tabs'));
tabContents.forEach((tabCont) => {
  new Tabs(tabCont);
});
